import { Injectable, Optional } from '@angular/core';

import { Am5Chart } from '../../../Common/Charts/models/am5-chart.model';
import { Am5ChartConfiguration } from '../../../Common/Charts/models/am5-chart-configuration.model';
import { Am5ChartExportLabelService } from './am5-chart-export-label.service';
import { Am5DateValueXYChartConfiguration } from '../../../Common/Charts/models/Am5DateValueXYChart/am5-date-value-x-y-chart-configuration.model';
import { ChartDisplayElementModel } from '../../../Common/Reports/models/chart-display-element.model';
import { ChartModuleTypesEnum } from '../../../Common/Charts/enums/chart-module-types.enum';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { ReportBlockTypeEnum } from '../../../Common/Reports/enums/report-block-type.enum';
import {
    Am5CategoryValueXYBaseChartConfiguration
} from '../../../Common/Charts/models/Am5CategoryValueXYChart/am5-category-value-x-y-base-chart-configuration.model';
import { ChartSpecificTypesEnum } from '../enums/chart-specific-types.enum';
import {
    Am5CategoryValueXYBarChartConfiguration
} from '../../../Common/Charts/models/Am5CategoryValueXYChart/Am5CategoryValueXYBarChart/am5-category-value-x-y-bar-chart-configuration.model';
import {
    Am5CategoryValueXYColumnChartConfiguration
} from '../../../Common/Charts/models/Am5CategoryValueXYChart/Am5CategoryValueXYColumnChart/am5-category-value-x-y-column-chart-configuration.model';


@Injectable()
export class Am5ChartConfigurationService {

    constructor(
        public localizationService: LocalizationService,
        @Optional() public am5ChartExportLabelService: Am5ChartExportLabelService
    ) {}

    getChartConfiguration(id: string, type: ReportBlockTypeEnum, displayElement?: ChartDisplayElementModel): Am5ChartConfiguration {
        let config;
        switch (type) {
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON:
            case ReportBlockTypeEnum.COVERAGE_IMPACT_TREND:
                const isComparison = type === ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON;
                return {
                    id: id,
                    type: type as ReportBlockTypeEnum,
                    moduleType: ChartModuleTypesEnum.REPORTS,
                    padding: {},
                    header: {
                        hasHeader: true,
                        headlessHeadingTop: 10,
                        titleLabel: {},
                        dateRangeLabelText: displayElement?.dateRangeLabel,
                        ...(isComparison ? {} : {
                            primarySeriesLabelText: displayElement?.series[0]?.label,
                            primarySeriesUrl: displayElement?.series[0]?.getSeriesUrl(),
                        }),
                    },
                    noContent: {
                        label: this.localizationService.transform('Monitoring.result.noContentFound')
                    },
                    hasLegend: true,
                    legendFontSize: isComparison ? '0.75rem': undefined,
                    useChartCursor: false,
                    useScrollbar: true,
                    useTooltip: true,
                    getExportConfiguration: (chart: Am5Chart) => {
                        return {
                            dataFields: this.am5ChartExportLabelService?.getExportLabels(type, chart, displayElement)
                        };
                    },
                } as Am5DateValueXYChartConfiguration;
            case ReportBlockTypeEnum.SENTIMENT_TREND:
                return {
                    id: id,
                    type: type as ReportBlockTypeEnum,
                    moduleType: ChartModuleTypesEnum.REPORTS,
                    padding: {},
                    header: {
                        hasHeader: true,
                        headlessHeadingTop: 30,
                        titleLabel: {},
                        dateRangeLabelText: displayElement?.dateRangeLabel,
                        primarySeriesLabelText: displayElement?.series[0].label,
                        primarySeriesUrl: displayElement?.series[0].getSeriesUrl(),
                    },
                    noContent: {
                        label: this.localizationService.transform('Monitoring.result.noContentFound')
                    },
                    isStacked: true,
                    hasLegend: true,
                    centerLegend: true,
                    legendItemWidth: 125,
                    useChartCursor: false,
                    useTooltip: true,
                    getExportConfiguration: (chart: Am5Chart) => {
                        return {
                            dataFields: this.am5ChartExportLabelService?.getExportLabels(type, chart, displayElement)
                        };
                    }
                } as Am5DateValueXYChartConfiguration;
            case ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME:
            case ReportBlockTypeEnum.TOP_10_OUTLETS:
            case ReportBlockTypeEnum.TOP_HASHTAGS:
            case ReportBlockTypeEnum.TOP_SOCIAL_REFERRERS:
                const isSocialReferrers = type === ReportBlockTypeEnum.TOP_SOCIAL_REFERRERS;
                config = {
                    id: id,
                    type: type as ReportBlockTypeEnum,
                    moduleType: ChartModuleTypesEnum.REPORTS,
                    padding: {},
                    header: {
                        hasHeader: true,
                        headlessHeadingTop: 10,
                        titleLabel: {},
                        dateRangeLabelText: displayElement?.dateRangeLabel,
                        primarySeriesLabelText: displayElement?.series[0].label,
                        primarySeriesUrl: displayElement?.series[0].getSeriesUrl(),
                    },
                    noContent: {
                        label: this.localizationService.transform('Monitoring.result.noContentFound')
                    },
                    hasLegend: false,
                    useScrollbar: false,
                    useTooltip: true,
                    barDisplayLimit: isSocialReferrers ? 12 : 10,
                    // google analytics source names are long, give them their own line
                    seriesLabelOnNewLine: isSocialReferrers,
                    getExportConfiguration: (chart: Am5Chart) => {
                        return {
                            dataFields: this.am5ChartExportLabelService?.getExportLabels(type, chart, displayElement)
                        };
                    },
                } as Am5CategoryValueXYBaseChartConfiguration;
                if (this.isBarChart(displayElement?.chartSpecificType)) {
                    return {
                        ...config,
                        stackedCategoryLabel: true,
                        includeScrollbarX: false,
                        barHeight: 40,
                        showFewerItemsAtLimitedSpace: true
                    } as Am5CategoryValueXYBarChartConfiguration;
                } else {
                    return config as Am5CategoryValueXYColumnChartConfiguration;
                }
        }
    }

    private isBarChart(chartSpecificType: ChartSpecificTypesEnum): boolean {
        return chartSpecificType === ChartSpecificTypesEnum.BAR ||
            chartSpecificType === ChartSpecificTypesEnum.MULTI_STACKED_BAR;
    }
}
